import React, { useState, useRef } from 'react';
import { FiUser, FiCamera, FiUpload, FiX } from 'react-icons/fi';
import toast from 'react-hot-toast';

const AvatarUpload = ({ 
  currentAvatar, 
  onUpload, 
  loading = false, 
  size = 'medium',
  className = '' 
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentAvatar);
  const fileInputRef = useRef(null);

  const sizes = {
    small: 'w-16 h-16',
    medium: 'w-24 h-24',
    large: 'w-32 h-32'
  };

  const iconSizes = {
    small: 'w-4 h-4',
    medium: 'w-5 h-5',
    large: 'w-6 h-6'
  };

  const validateFile = (file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'text/xml'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    // Check file extension as fallback
    const fileName = file.name.toLowerCase();
    const validExtension = /\.(jpe?g|png|gif|webp|svg)$/i.test(fileName);

    if (!allowedTypes.includes(file.type) && !validExtension) {
      toast.error('Please upload a valid image file (JPEG, PNG, GIF, WebP, SVG)');
      return false;
    }

    if (file.size > maxSize) {
      toast.error('File size must be less than 5MB');
      return false;
    }

    return true;
  };

  const handleFileSelect = (file) => {
    if (!validateFile(file)) {
      return;
    }

    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);

    // Call the upload handler
    onUpload(file);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleRemoveAvatar = () => {
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onUpload(null); // Signal removal
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`relative ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        className="hidden"
      />

      <div className="flex flex-col items-center space-y-4">
        {/* Avatar Preview */}
        <div
          className={`
            relative ${sizes[size]} rounded-full overflow-hidden border-4 border-white shadow-lg
            ${dragOver ? 'border-primary-400 bg-primary-50' : 'border-gray-200'}
            ${loading ? 'opacity-50' : ''}
            cursor-pointer transition-all duration-200 hover:shadow-xl
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={openFileDialog}
        >
          {previewUrl ? (
            <>
              <img
                src={previewUrl}
                alt="Avatar"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                <FiCamera className={`${iconSizes[size]} text-white opacity-0 hover:opacity-100 transition-opacity duration-200`} />
              </div>
            </>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary-100 to-secondary-100 flex items-center justify-center">
              <FiUser className={`${iconSizes[size]} text-primary-400`} />
            </div>
          )}

          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            </div>
          )}
        </div>

        {/* Upload Actions */}
        <div className="flex items-center space-x-2">
          <button
            onClick={openFileDialog}
            disabled={loading}
            className="btn btn-primary flex items-center space-x-2 text-sm disabled:opacity-50"
          >
            <FiUpload className="w-4 h-4" />
            <span>{previewUrl ? 'Change' : 'Upload'}</span>
          </button>

          {previewUrl && (
            <button
              onClick={handleRemoveAvatar}
              disabled={loading}
              className="btn btn-secondary flex items-center space-x-2 text-sm disabled:opacity-50"
            >
              <FiX className="w-4 h-4" />
              <span>Remove</span>
            </button>
          )}
        </div>

        {/* Drag & Drop Hint */}
        {dragOver && (
          <div className="absolute inset-0 flex items-center justify-center bg-primary-50 bg-opacity-90 rounded-lg border-2 border-dashed border-primary-300">
            <div className="text-center">
              <FiUpload className="w-8 h-8 text-primary-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-primary-700">Drop image here</p>
            </div>
          </div>
        )}
      </div>

      {/* Upload Guidelines */}
      <div className="mt-4 text-xs text-gray-500 text-center max-w-xs">
        <p>Upload a square image for best results</p>
        <p>Maximum file size: 5MB</p>
        <p>Supported formats: JPEG, PNG, GIF, WebP, SVG</p>
      </div>
    </div>
  );
};

export default AvatarUpload;